/**
 * Members and invitations — the settings screen where a community runs its own staff list.
 *
 * Inviting takes a role by id or by key because the two callers are different people: the
 * management app posts the id of a role it just listed, while a setup script or a test names the
 * system role it means. Exactly one must be given, so the server never has to guess which wins.
 */

import { z } from "zod";
import { GRANT_SCOPES } from "./permissions.js";
import { emailSchema, instantSchema, ulidSchema } from "./primitives.js";

/** A system role key — `organiser`, `registrar` — or a community-defined role's key. */
export const roleKeySchema = z
  .string()
  .min(1)
  .max(60)
  .regex(/^[a-z][a-z0-9_]*$/, "Not a role key");

/** Exactly one of `roleId` and `roleKey`, for the two callers described above. */
const NAME_THE_ROLE_ONCE = {
  message: "Name the role once — by id or by key, not both and not neither",
  path: ["roleId"],
};

export const inviteMemberSchema = z
  .object({
    email: emailSchema,
    roleId: ulidSchema.optional(),
    roleKey: roleKeySchema.optional(),
  })
  .refine((value) => (value.roleId == null) !== (value.roleKey == null), NAME_THE_ROLE_ONCE);

export const changeMemberRoleSchema = z.object({
  roleId: ulidSchema,
});

/**
 * Narrow a member to one event, or broaden them back. `eventId: null` is the broadening — the
 * grant returns to community scope — which is why this is not simply an optional field.
 */
export const limitMemberAccessSchema = z.object({
  eventId: ulidSchema.nullable(),
});

/**
 * Give someone a role over one event, one competition, one session or one category (D42).
 *
 * Additive, unlike `changeMemberRoleSchema`: one row per responsibility, because the same
 * volunteer really is the registrar for Dance and nothing at all for Drum.
 *
 * The two impossible combinations are refused here as well as in the repository — a scope narrower
 * than the community with nothing named, and a community-wide grant that names something. The
 * repository is the boundary that matters and checks both again; what this adds is a message
 * attached to `scopeId`, so the screen puts the error on the control the person has to fix rather
 * than at the top of the dialog (ux.md §3.2).
 */
export const grantRoleSchema = z
  .object({
    roleId: ulidSchema.optional(),
    roleKey: roleKeySchema.optional(),
    scopeType: z.enum(GRANT_SCOPES),
    /** Null and absent mean the same thing here: nothing was narrowed to. */
    scopeId: ulidSchema.nullable().optional(),
    /** A grant that lapses on its own — the weekend judge who should not still be one in October. */
    expiresAt: instantSchema.nullable().optional(),
  })
  .refine((value) => (value.roleId == null) !== (value.roleKey == null), NAME_THE_ROLE_ONCE)
  .refine((value) => value.scopeType === "community" || value.scopeId != null, {
    message: "Say which one this is for",
    path: ["scopeId"],
  })
  .refine((value) => value.scopeType !== "community" || value.scopeId == null, {
    message: "A community-wide role is not narrowed to anything",
    path: ["scopeId"],
  });

export type InviteMemberInput = z.infer<typeof inviteMemberSchema>;
export type GrantRoleInput = z.infer<typeof grantRoleSchema>;
